import type { ReactNode } from "react";
import {
  Checkbox,
  cn,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "../ui";
import { ComboBox } from "../common/combo-box";
import { IconPickerButton } from "../common/icon-picker";
import { DEFAULT_ICON_COLOR } from "~/utils/lucide-icons";
import { NodeTableColumnFormField } from "./custom/node-table-column-form-field";
import { NodeMapSettingFormField } from "./custom/node-map-setting-form-field";
import { CoordinatesFormField } from "./custom/coordinates-form-field";
import { CompanyLogoFormField } from "./custom/company-logo-form-field";
import { CodeMirrorFormField } from "./custom/code-mirror-form-field";
import { CodeMirrorDialogFormField } from "./custom/code-mirror-dialog-form-field";
import type { UseFormReturn } from "react-hook-form";
import LocationPickerDialog from "../common/location-picker-dialog";
import type { LatLngLiteral } from "leaflet";
import { getHexInputOptions, isBulkFormData } from "~/utils/form-util";
import type {
  FormStructure,
  FormOption,
  SensorSelectType,
  ScriptLookupType,
} from "~/types/ui";
import { StringifySelect } from "../common/stringify-select";
import { ContactSelect } from "./custom/contact-select";
import type { ActionEventType, Contact } from "~/types/api";
import ActionResources from "./actions/action-resources-form-field";
import { ActionTypeEnum, actionTypeValues } from "~/utils/action-util";
import ActionConfigSelector from "./actions/action-config-form-field";
import ReportResources from "./reports/report-resources-form-field";
import ReportTags from "./reports/report-tags-form-field";
import type { ResourceType, ReportSoTemplate } from "~/types/api";
import { CustomFileInput } from "../common/file-input";
import { Switch } from "../ui/switch";
import InstallationImageFormField from "../nodes/details/installation-image-form-field";
import SpotIOFormField from "../nodes/details/spot-io-form-field";
import AttributeFormField from "../nodes/details/attribute-form-field";
import ModbusFormField from "../nodes/details/modbus-form-field";
import SensorValueFormField from "../nodes/details/sensor-value-form-field";
import SparkplugFormField from "../nodes/details/sparkplug-form-field";
import MqttFormField from "../nodes/details/mqtt-form-field";
import FormFieldDialog from "../common/form-field-dialog";
import { InformationDialog } from "../common/information-dialog";
import AlarmConditionsFormField from "./alarms/alarm-conditions-form-field";
import type { AdvancedAlarmLogicalOperatorEnum, NodeDto } from "~/types/api";

export enum FormFieldType {
  TEXT = "text",
  NUMBER = "number",
  EMAIL = "email",
  COLOR = "color",
  SELECT = "select",
  TEXTAREA = "textarea",
  CHECKBOX = "checkbox",
  SWITCH = "switch",
  DATE = "date",
  FILE = "file",
  PASSWORD = "password",
  COMBOBOX = "combobox",
  MULTI_COMBOBOX = "multi-combobox",
  STRINGIFY_SELECT = "stringify-select",
  CONTACT_SELECT = "contact-select",

  // Custom Types For Specific Use Cases
  CORDINATES = "cords",
  SENSOR_SELECT = "sensor-selector",

  /** A Lucide icon chosen by name from the full set — stores the kebab-case id. */
  ICON = "icon",

  // Node Specific Types
  HEX = "hex",
  NODETYPE = "nodetype",
  INSTALLATION_IMAGE = "installation_image",
  SPOT_IO = "spot_io",
  MODBUS_REGISTERS = "modbus_registers",
  ATTRIBUTES = "attributes",
  SENSOR_VALUE = "sensor_value",
  MQTT_PUBLISH_AND_SUBSCRIBE = "mqtt_publish_and_subscribe",
  MQTT_PUBLISH = "mqtt_publish",
  MQTT_SUBSCRIBE = "mqtt_subscribe",
  SPARKPLUG = "sparkplug",

  //Action Specific Types
  ACTION_CONFIG = "action_config",
  ACTION_RESOURCES = "action_resources",

  //Report Specific Types
  REPORT_RESOURCES = "report_resources",
  REPORT_TAGS = "report_tags",

  // Alarm Specific Types
  ALARM_CONDITIONS = "alarm_conditions",

  // Setting specific types
  SETTING_NODE_TABLE_COLUMNS = "setting_node_table_columns",
  SETTING_NODE_MAP = "setting_node_map",
  COORDINATES_STRING = "coordinates_string",
  SETTING_WHITE_LABEL_IMAGE = "setting_white_label_image",

  MULTI_APP = "multiple_applications",

  CODEMIRROR = "codemirror",
  DIALOG_CODEMIRROR = "dialog_codemirror",
}

export function getField<T>(
  t: (key: string, options?: Record<string, unknown>) => string,
  s: FormStructure<T>,
  k: keyof T | "virtualFields",
  field: any,
  form: UseFormReturn<any>,
  rowIndex?: number | undefined,
): ReactNode {
  return s[k]?.dialog ? (
    <FormFieldDialog
      title={s[k]?.label ? t(s[k].label) : undefined}
      description={s[k]?.dialogDescription}
    >
      {_getField<T>(t, s, k, field, form, rowIndex)}
    </FormFieldDialog>
  ) : (
    _getField<T>(t, s, k, field, form, rowIndex)
  );
}

function _getField<T>(
  t: (key: string, options?: Record<string, unknown>) => string,
  s: FormStructure<T>,
  k: keyof T | "virtualFields",
  field: any,
  form: UseFormReturn<any>,
  rowIndex?: number | undefined,
): ReactNode {
  let tp = s[k]?.type;

  // This makes it possible to modify the type based on other fields and values in the form
  // E.g. show a hex input field if the node type is lorawan but a text field if it is anything else
  if (s[k]?.modifyTypeFn) {
    tp = s[k].modifyTypeFn(form);
  }

  // Handle virtual fields
  if (k === "virtualFields" && s.virtualFields) {
    const vf = s.virtualFields.find((v) => v.name === field.name);
    if (vf) {
      tp = vf.type;
    }
  }

  if (s[k]?.hiddenFn && s[k]?.hiddenFn(form, rowIndex)) {
    return null;
  }

  let disabled = s[k]?.disabled ?? false;
  if (s[k]?.disabledFn) {
    disabled = s[k]?.disabledFn(form, field) ?? false;
  }

  const placeholder = s[k]?.placeholder
    ? t(s[k].placeholder, s[k].placeholderOptions)
    : "";
  switch (tp) {
    case FormFieldType.TEXT:
      return (
        <Input
          id={field.id}
          className={s[k]?.className ?? ""}
          key={field.id}
          placeholder={placeholder}
          {...field}
          disabled={disabled ?? false}
        />
      );

    // Stores Lucide's kebab-case id (`"house"`),
    case FormFieldType.ICON:
      return (
        <IconPickerButton
          key={field.id}
          value={field.value}
          allowClear={!s[k]?.required}
          onChange={(name) => field.onChange(name ?? "")}
        />
      );

    case FormFieldType.COLOR:
      return (
        <Input
          id={field.id}
          key={field.id}
          type="color"
          className={cn("h-9 w-16 p-1", s[k]?.className)}
          {...field}
          value={field.value || DEFAULT_ICON_COLOR}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.EMAIL:
      return (
        <Input
          id={field.id}
          key={field.id}
          type="email"
          placeholder={placeholder}
          className={s[k]?.className ?? ""}
          {...field}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.NUMBER:
      return (
        <Input
          id={field.id}
          key={field.id}
          type="number"
          className={s[k]?.className ?? ""}
          placeholder={placeholder}
          {...field}
          // A null value is legitimate for a nullable number (e.g. an unset alarm
          // limit); map it to "" so the input stays controlled.
          value={field.value ?? ""}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.FILE:
      return (
        <CustomFileInput
          key={field.id}
          value={
            typeof field.value === "string"
              ? field.value
              : ((field.value as File)?.name ?? "")
          }
          placeholder={placeholder || t("common.upload")}
          onChange={(file) => field.onChange(file)}
          accept={
            s[k].options?.find((x) => x.label == "accept")?.value as string
          }
          className={s[k]?.className ?? ""}
        />
      );

    case FormFieldType.PASSWORD:
      return (
        <Input
          id={field.id}
          key={field.id}
          type="password"
          placeholder="**********"
          className={s[k]?.className ?? ""}
          {...field}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.CHECKBOX:
      return isBulkFormData(form?.getValues()) && rowIndex != undefined ? (
        <Checkbox
          key={field.id}
          id={field.id}
          value={field.value}
          checked={field.value}
          onCheckedChange={field.onChange}
          className={s[k]?.className ?? ""}
          disabled={disabled ?? false}
        />
      ) : (
        <Switch
          key={field.id}
          id={field.id}
          value={field.value}
          checked={field.value}
          onCheckedChange={field.onChange}
          className={s[k]?.className ?? ""}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.SWITCH:
      return (
        <Switch
          key={field.id}
          id={field.id}
          value={field.value}
          checked={field.value}
          onCheckedChange={field.onChange}
          className={s[k]?.className ?? ""}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.TEXTAREA:
      return (
        <textarea
          id={field.id}
          key={field.id}
          placeholder={placeholder}
          className={
            "w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" +
            (s[k]?.className ? " " + s[k].className : "")
          }
          {...field}
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.COMBOBOX:
      return (
        <ComboBox
          key={field.id}
          options={(s[k].options as FormOption[]) ?? []}
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          className={s[k]?.className ?? ""}
          placeholder={placeholder}
          disabled={disabled ?? false}
          {...field}
        />
      );

    case FormFieldType.MULTI_COMBOBOX:
      return (
        <ComboBox
          key={field.id}
          options={(s[k].options as FormOption[]) ?? []}
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          className={s[k]?.className ?? ""}
          placeholder={placeholder}
          disabled={disabled ?? false}
          clearable={!disabled}
          {...field}
          isMulti
        />
      );

    case FormFieldType.STRINGIFY_SELECT:
      const settings = s[k].options?.find((x) => x.label == "settings")?.value;
      const filteredOptions =
        isBulkFormData(form?.getValues()) && rowIndex != undefined
          ? s[k].options?.find((x) => x.label == "options")?.value[
              form?.getValues()?.items?.at(rowIndex)[
                settings?.typeKey as string
              ]
            ]
          : s[k].options?.find((x) => x.label == "options")?.value[
              form?.getValues(settings?.typeKey as string)
            ];

      return (
        <StringifySelect
          key={field.id}
          options={[
            { label: "settings", value: settings },
            { label: "options", value: filteredOptions },
          ]}
          value={field.value ?? ""}
          onChange={(val: any) => field.onChange(val)}
          className={s[k]?.className ?? ""}
          placeholder={placeholder}
          disabled={disabled ?? false}
          {...field}
        />
      );

    case FormFieldType.CONTACT_SELECT:
      return (
        <ContactSelect
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          options={
            (s[k].options?.find((x) => x.label == "contacts")
              ?.value as Contact[]) ?? []
          }
          canCreate={!isBulkFormData(form?.getValues())}
        />
      );

    case FormFieldType.ACTION_CONFIG: {
      const actionTypes = (
        isBulkFormData(form?.getValues()) && rowIndex != undefined
          ? form?.getValues()?.items?.at(rowIndex)["actionTypesString"]
          : form?.getValues("actionTypesString")
      ) as string;
      const eventType = (
        isBulkFormData(form?.getValues()) && rowIndex != undefined
          ? form?.getValues()?.items?.at(rowIndex)["eventType"]
          : form?.getValues("eventType")
      ) as number;
      return (
        <ActionConfigSelector
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          options={[
            { label: "eventType", value: eventType },
            {
              label: "brokers",
              value:
                s[k].options?.find((x) => x.label == "brokers")?.value ?? [],
            },
          ]}
          email={actionTypes?.includes(actionTypeValues(ActionTypeEnum.EMAIL))}
          mqtt={actionTypes?.includes(actionTypeValues(ActionTypeEnum.MQTT))}
        />
      );
    }

    case FormFieldType.ACTION_RESOURCES:
      return (
        <ActionResources
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          options={(s[k].options as { label: string; value: any[] }[]) ?? []}
          eventType={
            (isBulkFormData(form?.getValues()) && rowIndex != undefined
              ? form?.getValues()?.items?.at(rowIndex)["eventType"]
              : form?.getValues("eventType")) as ActionEventType
          }
        />
      );

    case FormFieldType.ALARM_CONDITIONS:
      return (
        <AlarmConditionsFormField
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          options={
            (s[k].options as { label: string; value: NodeDto[] }[]) ?? []
          }
          logicalOperator={
            (isBulkFormData(form?.getValues()) && rowIndex != undefined
              ? form?.getValues()?.items?.at(rowIndex)["logicalOperator"]
              : form?.getValues(
                  "logicalOperator",
                )) as AdvancedAlarmLogicalOperatorEnum
          }
        />
      );

    case FormFieldType.REPORT_RESOURCES: {
      const reportTemplates =
        (s[k].options?.find((x) => x.label == "templates")
          ?.value as ReportSoTemplate[]) ?? [];
      const selectedTemplateId = (
        isBulkFormData(form?.getValues()) && rowIndex != undefined
          ? form?.getValues()?.items?.at(rowIndex)["reportSoTemplateId"]
          : form?.getValues("reportSoTemplateId")
      ) as string;
      const selectedTemplate = reportTemplates.find(
        (tmpl) => tmpl.reportSoTemplateId === selectedTemplateId,
      );

      return (
        <ReportResources
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          includedResourceTypes={
            (selectedTemplate?.resourceTypes as ResourceType[]) ?? []
          }
        />
      );
    }

    case FormFieldType.REPORT_TAGS:
      return (
        <ReportTags
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          options={(s[k].options as { label: string; value: any[] }[]) ?? []}
        />
      );

    case FormFieldType.MULTI_APP:
      return (
        <ComboBox
          key={field.id}
          options={(s[k].options as FormOption[]) ?? []}
          value={field.value ?? ""}
          onChange={(val) => {
            let res: number[] = [];
            if (val && Array.isArray(val)) res = val as number[];
            else if (val) res = [val as number];

            field.onChange(res);
          }}
          className={s[k]?.className ?? ""}
          placeholder={placeholder}
          disabled={disabled ?? false}
          isMulti
          {...field}
        />
      );
    case FormFieldType.CORDINATES: {
      // Use virtualFields to figure out mapping
      const vf =
        k === "virtualFields" && s.virtualFields
          ? s.virtualFields.find((v) => v.name === field.name)
          : undefined;

      const latKey =
        vf?.keys?.find(
          (d) =>
            d.toString().toLowerCase() === "latitude" ||
            d.toString().toLowerCase() === "lat",
        ) ?? null;

      const lonKey =
        vf?.keys?.find(
          (d) =>
            d.toString().toLowerCase() === "longitude" ||
            d.toString().toLowerCase() === "lon" ||
            d.toString().toLowerCase() === "lng",
        ) ?? null;

      if (!latKey || !lonKey) {
        console.error("No lat/lon keys found in virtual field", vf);
        return <div>Invalid coordinate field</div>;
      }

      if (form === null) return <div>Assert Failed</div>;

      const control = form.control;
      return (
        <div className="flex gap-2">
          <FormField
            control={control as any}
            name={latKey as any}
            render={({ field: f }) => (
              <Input
                id={field.id + "_lat"}
                type="number"
                step="any"
                placeholder="Latitude"
                className={s[k]?.className ?? ""}
                {...f}
                disabled={disabled ?? false}
              />
            )}
          />
          <FormField
            control={control as any}
            name={lonKey as any}
            render={({ field: f }) => (
              <Input
                id={field.id + "_lon"}
                type="number"
                step="any"
                placeholder="Longitude"
                className={s[k]?.className ?? ""}
                {...f}
                disabled={disabled ?? false}
              />
            )}
          />
          <LocationPickerDialog
            latitude={(form?.getValues(latKey as any) as number) ?? null}
            longitude={(form?.getValues(lonKey as any) as number) ?? null}
            onChange={(cords: LatLngLiteral | null) => {
              if (cords) {
                form?.setValue(latKey as any, cords.lat as any, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                form?.setValue(lonKey as any, cords.lng as any, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }
            }}
          />
        </div>
      );
    }

    case FormFieldType.SENSOR_SELECT: {
      const sensorSelect = s[k].options?.find((x) => x.label == "sensorSelect")
        ?.value as SensorSelectType;
      const deveui =
        isBulkFormData(form?.getValues()) && rowIndex != undefined
          ? form?.getValues()?.items?.at(rowIndex)[sensorSelect?.deveuiKey]
          : form?.getValues()[sensorSelect?.deveuiKey];

      return (
        <ComboBox
          key={field.id}
          options={
            (sensorSelect?.sensors
              ? sensorSelect?.sensors
                  .filter((x) => x.deveui == deveui)
                  .map((x) => x.tagKey)
              : []) ?? []
          }
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          className={s[k]?.className ?? ""}
          placeholder={placeholder}
          disabled={disabled ?? false}
          {...field}
        />
      );
    }

    case FormFieldType.SENSOR_VALUE: {
      const key =
        isBulkFormData(form?.getValues()) && rowIndex != undefined
          ? form?.getValues()?.items?.at(rowIndex)["key"]
          : form?.getValues()["key"];

      return (
        <SensorValueFormField
          value={field.value ?? ""}
          options={s[k].options?.concat([
            { label: "key", value: key } as FormOption<string>,
          ])}
        />
      );
    }

    case FormFieldType.SETTING_NODE_TABLE_COLUMNS:
      return (
        <NodeTableColumnFormField
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          options={(s[k].options as { label: string; value: any }[]) ?? []}
        />
      );

    case FormFieldType.SETTING_NODE_MAP:
      return (
        <NodeMapSettingFormField
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          options={(s[k].options as FormOption[]) ?? []}
        />
      );

    case FormFieldType.COORDINATES_STRING:
      return (
        <CoordinatesFormField
          value={field.value ?? ""}
          onChange={field.onChange}
        />
      );

    case FormFieldType.SETTING_WHITE_LABEL_IMAGE:
      return (
        <CompanyLogoFormField
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          global={
            s[k].options?.find((o) => o.label === "global")?.value === true
          }
        />
      );

    case FormFieldType.HEX:
      let hexBytes = 8;
      if (s[k].options) hexBytes = getHexInputOptions(s[k].options);

      const maxChars = hexBytes * 2;
      return (
        <Input
          {...field}
          placeholder={placeholder || "Enter identifier"}
          maxLength={maxChars}
          onChange={(e) => {
            const input = e.target.value.replace(/[^0-9a-fA-F]/g, "");
            field.onChange(input.toUpperCase());
            field.shouldValidate = true;
          }}
          className={
            cn("font-mono tracking-wider", field.error && "border-red-500") +
            (s[k]?.className ? " " + s[k].className : "")
          }
          disabled={disabled ?? false}
        />
      );

    case FormFieldType.INSTALLATION_IMAGE:
      return (
        <InstallationImageFormField
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          deveui={
            isBulkFormData(form?.getValues()) && rowIndex != undefined
              ? form?.getValues()?.items?.at(rowIndex)["deveui"]
              : form?.getValues()["deveui"]
          }
        />
      );

    case FormFieldType.SPOT_IO:
      return (
        <SpotIOFormField
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          options={s[k].options ?? []}
          deveui={
            isBulkFormData(form?.getValues()) && rowIndex != undefined
              ? form?.getValues()?.items?.at(rowIndex)["deveui"]
              : form?.getValues()["deveui"]
          }
        />
      );

    case FormFieldType.MODBUS_REGISTERS:
      return (
        <ModbusFormField
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          options={s[k].options ?? []}
        />
      );

    case FormFieldType.MQTT_PUBLISH_AND_SUBSCRIBE:
      return (
        <MqttFormField
          value={field.value ?? {}}
          onChange={(val) => field.onChange(val)}
          options={s[k].options ?? []}
          publish
          subscribe
        />
      );

    case FormFieldType.MQTT_PUBLISH:
      return (
        <MqttFormField
          value={field.value ?? {}}
          onChange={(val) => field.onChange(val)}
          options={s[k].options ?? []}
          publish
        />
      );

    case FormFieldType.MQTT_SUBSCRIBE:
      return (
        <MqttFormField
          value={field.value ?? {}}
          onChange={(val) => field.onChange(val)}
          options={s[k].options ?? []}
          subscribe
        />
      );

    case FormFieldType.SPARKPLUG:
      return (
        <SparkplugFormField
          value={field.value ?? {}}
          onChange={(val) => field.onChange(val)}
          options={s[k].options ?? []}
        />
      );

    case FormFieldType.ATTRIBUTES:
      return (
        <AttributeFormField
          value={field.value ?? []}
          onChange={(val) => field.onChange(val)}
          options={[...new Set(s[k].options?.map((x) => x.label) ?? [])]}
          deveui={
            isBulkFormData(form?.getValues()) && rowIndex != undefined
              ? form?.getValues()?.items?.at(rowIndex)["deveui"]
              : form?.getValues()["deveui"]
          }
        />
      );

    case FormFieldType.CODEMIRROR:
      return (
        <CodeMirrorFormField
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          options={
            (s[k].options as {
              label: string;
              value: ScriptLookupType[];
            }[]) ?? []
          }
        />
      );

    case FormFieldType.DIALOG_CODEMIRROR:
      return (
        <CodeMirrorDialogFormField
          value={field.value ?? ""}
          onChange={(val) => field.onChange(val)}
          options={
            (s[k].options as {
              label: string;
              value: ScriptLookupType[];
            }[]) ?? []
          }
        />
      );

    default:
      return undefined;
  }
}

export function generateFields<T>(
  form: UseFormReturn<
    {
      [x: string]: any;
    },
    any,
    {
      [x: string]: any;
    }
  >,
  structure: FormStructure<T>,
  t: (key: string, options?: Record<string, unknown>) => string,
): ReactNode[] {
  const fields: ReactNode[] = [];

  // Collect keys that are covered by virtual fields
  const coveredKeys = new Set<keyof T>();
  structure.virtualFields?.forEach((vf) =>
    vf.keys.forEach((k) => coveredKeys.add(k)),
  );

  for (const [key, def] of Object.entries(structure) as [
    keyof T | "virtualFields",
    any,
  ][]) {
    if (key === "virtualFields") continue;
    if (coveredKeys.has(key as keyof T)) continue;
    if (def.hidden) continue;

    if (
      structure[key]?.hiddenFn &&
      structure[key].hiddenFn(form as UseFormReturn<any>)
    )
      continue;

    fields.push(
      <FormField
        key={key as string}
        control={form.control}
        name={key as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t(def.label)}
              {def.information && (
                <InformationDialog {...def?.informationProps} />
              )}
            </FormLabel>
            <FormControl>
              {getField(t, structure, key, field, form)}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />,
    );
  }

  // Render virtual fields
  structure.virtualFields?.forEach((vf) => {
    fields.push(
      <FormField
        key={vf.name}
        control={form.control}
        name={vf.name as any}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t(vf.label)}</FormLabel>
            <FormControl>
              {getField(t, structure, "virtualFields", field, form)}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />,
    );
  });

  return fields;
}
